/**
 * Public read-only viewer for tokenized links shared over WhatsApp.
 * Route: /p/:token  (no auth required)
 *
 * Renders a clean printable card for the entity: invoice, order, PO,
 * statement, or product.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertCircle, Building2, Receipt, Package, ShoppingCart, Truck, FileText } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmt = (v, moneda = "CRC") => {
  const n = Number(v) || 0;
  if (moneda === "USD") return `USD ${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `₡${n.toLocaleString("es-CR", { maximumFractionDigits: 0 })}`;
};

const ICON_BY_TYPE = {
  invoice: Receipt,
  order: ShoppingCart,
  purchase_order: Truck,
  statement: FileText,
  product: Package,
};

const TITLE_BY_TYPE = {
  invoice: "Factura",
  order: "Pedido",
  purchase_order: "Orden de Compra",
  statement: "Estado de Cuenta",
  product: "Producto",
};

export default function PublicView() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/public/${token}`);
        if (!cancelled) setState({ loading: false, data, error: "" });
      } catch (e) {
        if (!cancelled) setState({
          loading: false,
          data: null,
          error: e?.response?.data?.detail || "Enlace no disponible",
        });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (state.loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
    </div>
  );

  if (state.error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-md text-center">
        <AlertCircle className="h-10 w-10 text-amber-500 mx-auto" />
        <h1 className="text-xl font-black mt-3" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          {state.error}
        </h1>
        <p className="text-sm text-slate-500 mt-2">El enlace puede haber expirado o sido revocado por el comercio.</p>
      </div>
    </div>
  );

  const { entity_type, company, data, tier } = state.data;
  const Icon = ICON_BY_TYPE[entity_type] || FileText;
  const title = TITLE_BY_TYPE[entity_type] || "Documento";
  const showWatermark = tier !== "pro"; // Premium shows it; Pro removes it.

  return (
    <div className="min-h-screen bg-slate-50 print:bg-white">
      {/* Top bar */}
      <header className="bg-[#0B1F3A] text-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-white/10 grid place-items-center">
            <Building2 className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="text-sm text-white/70">Documento de</div>
            <div className="font-black truncate" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>{company.nombre}</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-slate-200 flex items-center gap-3">
            <Icon className="h-6 w-6 text-[#0B1F3A]" />
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">{title}</div>
              <div className="font-black text-xl" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                {data.numero || data.codigo || data.nombre_cliente || ""}
              </div>
            </div>
          </div>

          {(entity_type === "invoice" || entity_type === "order") && (
            <div className="px-4 sm:px-6 py-4">
              <div className="text-sm text-slate-600">Cliente: <span className="font-semibold text-slate-900">{data.nombre_cliente || "—"}</span></div>
              {data.fecha && <div className="text-sm text-slate-600">Fecha: {data.fecha}</div>}
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-500 uppercase text-xs">
                      <th className="py-2">Código</th>
                      <th className="py-2">Producto</th>
                      <th className="py-2 text-right">Cant.</th>
                      <th className="py-2 text-right">P/U</th>
                      <th className="py-2 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.lineas || []).map((l, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-1.5">{l.codigo}</td>
                        <td className="py-1.5">{l.producto || l.descripcion} {l.es_bonificacion && <span className="text-emerald-700 text-xs ml-1">GRATIS</span>}</td>
                        <td className="py-1.5 text-right">{l.cantidad}</td>
                        <td className="py-1.5 text-right">{fmt(l.precio_unitario)}</td>
                        <td className="py-1.5 text-right">{fmt(l.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex flex-col items-end gap-1">
                <div className="text-lg"><span className="text-slate-500">Total: </span><span className="font-black">{fmt(data.total)}</span></div>
                {data.saldo_pendiente !== undefined && data.saldo_pendiente > 0 && (
                  <div className="text-amber-700">Saldo pendiente: <span className="font-bold">{fmt(data.saldo_pendiente)}</span></div>
                )}
              </div>
            </div>
          )}

          {entity_type === "purchase_order" && (
            <div className="px-4 sm:px-6 py-4">
              <div className="text-sm text-slate-600">Proveedor: <span className="font-semibold">{data.proveedor_nombre}</span></div>
              <div className="text-sm text-slate-600">Fecha: {data.fecha}</div>
              <table className="w-full text-sm mt-3">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500 uppercase text-xs">
                    <th className="py-2">Código</th>
                    <th className="py-2">Descripción</th>
                    <th className="py-2 text-right">Cant.</th>
                    <th className="py-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.lineas || []).map((l, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-1.5">{l.codigo || l.codigo_interno}</td>
                      <td className="py-1.5">{l.descripcion}</td>
                      <td className="py-1.5 text-right">{l.cantidad}</td>
                      <td className="py-1.5 text-right">{fmt(l.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 text-right text-lg"><span className="text-slate-500">Total: </span><span className="font-black">{fmt(data.total)}</span></div>
            </div>
          )}

          {entity_type === "statement" && (
            <div className="px-4 sm:px-6 py-4">
              <div className="text-sm text-slate-600">Cliente: <span className="font-semibold">{data.nombre_cliente}</span></div>
              <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-amber-900">
                <span className="text-xs uppercase tracking-wide">Saldo pendiente total</span>
                <div className="text-2xl font-black" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>{fmt(data.total_pendiente)}</div>
              </div>
              <table className="w-full text-sm mt-4">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500 uppercase text-xs">
                    <th className="py-2">Factura</th>
                    <th className="py-2 text-right">Total</th>
                    <th className="py-2 text-right">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.invoices || []).map((i, idx) => (
                    <tr key={idx} className="border-b border-slate-100">
                      <td className="py-1.5">{i.numero}</td>
                      <td className="py-1.5 text-right">{fmt(i.total)}</td>
                      <td className="py-1.5 text-right">{fmt(i.saldo_pendiente)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {entity_type === "product" && (
            <div className="px-4 sm:px-6 py-4 space-y-2">
              {data.image_url && (
                <img src={data.image_url} alt={data.descripcion} className="w-full max-w-md mx-auto rounded-md" />
              )}
              <div className="text-2xl font-black text-center" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                {data.descripcion}
              </div>
              <div className="text-center text-slate-500 text-sm">Código: {data.codigo}</div>
              <div className="text-center text-3xl font-black text-[#0B1F3A]" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                {fmt(data.precio_venta)}
              </div>
            </div>
          )}
        </div>

        <div className="text-center text-xs text-slate-400 mt-6">
          Generado por {company.nombre}
        </div>

        {showWatermark && (
          <a
            href="https://minegocio.app"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="public-view-watermark"
            className="mt-4 mx-auto flex items-center justify-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-xs text-slate-600 hover:border-[#0B1F3A] hover:text-[#0B1F3A] hover:shadow-sm transition-all w-fit print:hidden"
          >
            <span className="h-5 w-5 rounded-md bg-[#0B1F3A] grid place-items-center">
              <span className="text-amber-300 font-black text-[10px]" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>M</span>
            </span>
            <span>
              Powered by <span className="font-bold text-[#0B1F3A]">MiNegocio</span> · Creá el catálogo digital de tu negocio gratis
            </span>
          </a>
        )}
      </main>
    </div>
  );
}
